import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel,
  Stack, Switch, TextField, Typography
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { toast } from 'react-toastify';
import { useAuth } from '../services/AuthService';

const EMPTY = {
  enabled: false,
  latest_version: '1.0.0',
  minimum_supported_version: '1.0.0',
  title: 'Atualização disponível',
  message: 'Uma nova versão do All Delivery está disponível.',
  play_store_url: 'https://play.google.com/store/apps/details?id=com.alldelivery.br',
  force_update: false,
  announcement_enabled: false,
  announcement_title: 'Nova atualização disponível',
  announcement_message: 'Atualize para usar os novos recursos do All Delivery.',
  announcement_url: ''
};

export default function AndroidAppUpdate() {
  const { api } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/app-updates/android/admin');
    setForm({ ...EMPTY, ...(data || {}) });
  }, [api]);

  useEffect(() => {
    load().catch((error) => toast.error(error.response?.data?.error || 'Erro ao carregar atualização'));
  }, [load]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async (publishAnnouncement = false) => {
    setSaving(true);
    try {
      const payload = publishAnnouncement
        ? { ...form, announcement_enabled: true, publish_announcement: true }
        : form;
      const { data } = await api.put('/app-updates/android/admin', payload);
      setForm({ ...EMPTY, ...data.settings });
      toast.success(publishAnnouncement ? 'Aviso publicado para os aplicativos e a PWA!' : 'Configuração de atualização salva!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar atualização');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 850, mx: 'auto' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: '#ecfdf5', color: '#059669', display: 'grid', placeItems: 'center' }}>
          <SystemUpdateAltIcon />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={900}>Atualização do aplicativo</Typography>
          <Typography color="text.secondary">Publique um aviso para motoboys e demais usuários do app e da PWA.</Typography>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        O aviso publicado aparece no app mobile e na PWA. O usuário pode fechá-lo com X; enquanto estiver em uma versão antiga, alguns recursos podem não funcionar corretamente.
      </Alert>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={2.5}>
            <FormControlLabel
              control={<Switch checked={form.enabled} onChange={(event) => set('enabled', event.target.checked)} />}
              label="Ativar verificação de versão do Android"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label="Versão mais recente" helperText="Exemplo: 1.2.0" value={form.latest_version} onChange={(event) => set('latest_version', event.target.value)} />
              <TextField fullWidth label="Versão mínima permitida" helperText="Exemplo: 1.0.0" value={form.minimum_supported_version} onChange={(event) => set('minimum_supported_version', event.target.value)} />
            </Stack>
            <TextField fullWidth label="Título" value={form.title} onChange={(event) => set('title', event.target.value)} inputProps={{ maxLength: 120 }} />
            <TextField fullWidth multiline minRows={4} label="Mensagem e novidades" value={form.message} onChange={(event) => set('message', event.target.value)} inputProps={{ maxLength: 1000 }} />
            <TextField fullWidth label="Link da Play Store" value={form.play_store_url} onChange={(event) => set('play_store_url', event.target.value)} />
            <FormControlLabel
              control={<Switch color="error" checked={form.force_update} onChange={(event) => set('force_update', event.target.checked)} />}
              label="Forçar atualização"
            />
            {form.force_update && (
              <Alert severity="warning">
                O aviso não terá o botão “Depois” quando a versão instalada estiver abaixo da versão mínima. Use somente quando versões antigas realmente deixarem de funcionar.
              </Alert>
            )}
            <Button variant="contained" size="large" onClick={save} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar versão do Android'}
            </Button>
            <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Aviso para todos os usuários</Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={<Switch checked={Boolean(form.announcement_enabled)} onChange={(event) => set('announcement_enabled', event.target.checked)} />}
                  label="Manter aviso publicado"
                />
                <TextField fullWidth label="Título do aviso" value={form.announcement_title} onChange={(event) => set('announcement_title', event.target.value)} inputProps={{ maxLength: 120 }} />
                <TextField fullWidth multiline minRows={3} label="Mensagem" helperText="Avise que é necessário atualizar para usar os recursos mais recentes." value={form.announcement_message} onChange={(event) => set('announcement_message', event.target.value)} inputProps={{ maxLength: 1000 }} />
                <TextField fullWidth label="Link do botão Atualizar (opcional)" helperText="Sem link, o app mobile abre a Play Store e a PWA recarrega para buscar a versão nova." value={form.announcement_url} onChange={(event) => set('announcement_url', event.target.value)} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="contained" color="success" size="large" onClick={() => save(true)} disabled={saving}>
                    {saving ? 'Publicando...' : 'Publicar nova atualização'}
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => save(false)} disabled={saving}>Salvar aviso sem republicar</Button>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
